import type { SocialProfile } from '@/lib/types';
import { Instagram, Linkedin, Facebook, Github, Youtube, Globe, CheckCircle } from 'lucide-react';

interface SocialLinksProps {
  profiles: SocialProfile[];
}

export function SocialLinks({ profiles }: SocialLinksProps) {
  const getIcon = (platform: string) => {
    const p = platform.toLowerCase();
    
    if (p.includes('instagram')) return <Instagram className="w-5 h-5" />;
    if (p.includes('linkedin')) return <Linkedin className="w-5 h-5" />;
    if (p.includes('facebook')) return <Facebook className="w-5 h-5" />;
    if (p.includes('github')) return <Github className="w-5 h-5" />;
    if (p.includes('youtube')) return <Youtube className="w-5 h-5" />;
    if (p.includes('tiktok')) return (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    );
    if (p.includes('twitter') || p === 'x') return (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    );
    if (p.includes('imdb')) return (
      <div className="text-xs font-bold bg-yellow-400 text-black px-1 rounded">
        IMDb
      </div>
    );
    
    // Default icon for unknown platforms
    return <Globe className="w-5 h-5" />;
  };

  const getPlatformColor = (platform: string) => {
    const p = platform.toLowerCase();
    if (p.includes('instagram')) return 'hover:bg-pink-50 hover:border-pink-300';
    if (p.includes('linkedin')) return 'hover:bg-blue-50 hover:border-blue-300';
    if (p.includes('facebook')) return 'hover:bg-blue-50 hover:border-blue-400';
    if (p.includes('youtube')) return 'hover:bg-red-50 hover:border-red-300';
    if (p.includes('tiktok')) return 'hover:bg-gray-900 hover:text-white';
    if (p.includes('twitter') || p === 'x') return 'hover:bg-gray-900 hover:text-white';
    return 'hover:bg-gray-50';
  };

  if (!profiles || profiles.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <span className="text-sm font-medium text-gray-700">Social Profiles</span>
      <div className="flex flex-wrap gap-2">
        {profiles.map((profile, index) => (
          <a
            key={index}
            href={profile.url}
            className={`flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-gray-200 transition-colors ${getPlatformColor(profile.platform)}`}
            target="_blank"
            rel="noopener noreferrer"
            title={`${profile.platform}${profile.username ? ` - @${profile.username}` : ''}`}
          >
            {getIcon(profile.platform)}
            <span className="text-sm text-gray-700">
              {profile.username || profile.platform}
            </span>
            {profile.verified && (
              <CheckCircle className="w-4 h-4 text-blue-500" />
            )}
            {profile.followers && (
              <span className="text-xs text-gray-400">{profile.followers}</span>
            )}
          </a>
        ))}
      </div>
    </div>
  );
}
